import type { TFunction } from 'i18next';

const INVARIANT_CODES_PATTERN = /Portfolio invariant violations:\s*(.+)/i;

const FAILED_INVARIANTS_PATTERN = /failed invariants:\s*(.+)/i;

export const parseValidationErrorCodes = (errors: string[]): string[] => {
  const codes = new Set<string>();

  for (const error of errors) {
    const normalized = error.trim();

    if (!normalized) {
      continue;
    }

    const invariantMatch = normalized.match(INVARIANT_CODES_PATTERN);

    if (invariantMatch) {
      invariantMatch[1].split(',').forEach((code) => codes.add(code.trim()));
      continue;
    }

    const failedInvariantsMatch = normalized.match(FAILED_INVARIANTS_PATTERN);

    if (failedInvariantsMatch) {
      failedInvariantsMatch[1]
        .split(',')
        .forEach((code) => codes.add(code.trim()));
      continue;
    }

    for (const code of Object.keys(KNOWN_VALIDATION_ERROR_KEYS)) {
      if (normalized.includes(code)) {
        codes.add(code);
      }
    }
  }

  return [...codes];
};

const KNOWN_VALIDATION_ERROR_KEYS: Record<string, string> = {
  NON_POSITIVE_MARKET_VALUE: 'validationErrors.nonPositiveMarketValue',
  NEGATIVE_QUANTITY: 'validationErrors.negativeQuantity',
  NO_SUB_ACCOUNTS: 'validationErrors.noSubAccounts',
  WEIGHTS_SUM_MISMATCH: 'validationErrors.weightsSumMismatch',
  INVALID_CURRENCY: 'validationErrors.invalidCurrency',
  NON_POSITIVE_PRICE: 'validationErrors.nonPositivePrice',
  INVALID_MODEL_PORTFOLIO_TYPE: 'validationErrors.invalidModelPortfolioType',
  ASSET_CLASS_TARGETS_SUM_MISMATCH:
    'validationErrors.assetClassTargetsSumMismatch',
};

const translateValidationCode = (code: string, t: TFunction): string => {
  const key = KNOWN_VALIDATION_ERROR_KEYS[code.trim()];

  if (key) {
    return t(key, { ns: 'bulkRebalance' });
  }

  return t('validationErrors.unknownCode', {
    ns: 'bulkRebalance',
    code: code.trim(),
  });
};

export const formatValidationErrorMessage = (
  error: string,
  t: TFunction,
): string => {
  const normalized = error.trim();

  if (!normalized) {
    return t('audit.validationFailedUnknown', { ns: 'bulkRebalance' });
  }

  const invariantMatch = normalized.match(INVARIANT_CODES_PATTERN);

  if (invariantMatch) {
    return [...new Set(invariantMatch[1].split(',').map((code) => code.trim()))]
      .map((code) => translateValidationCode(code, t))
      .join(' · ');
  }

  const failedInvariantsMatch = normalized.match(FAILED_INVARIANTS_PATTERN);

  if (failedInvariantsMatch) {
    return [
      ...new Set(
        failedInvariantsMatch[1].split(',').map((code) => code.trim()),
      ),
    ]
      .map((code) => translateValidationCode(code, t))
      .join(' · ');
  }

  for (const code of Object.keys(KNOWN_VALIDATION_ERROR_KEYS)) {
    if (normalized.includes(code)) {
      return translateValidationCode(code, t);
    }
  }

  return normalized;
};

export const formatValidationErrorMessages = (
  errors: string[],
  t: TFunction,
): string[] => {
  if (errors.length === 0) {
    return [t('audit.validationFailedUnknown', { ns: 'bulkRebalance' })];
  }

  return errors.map((error) => formatValidationErrorMessage(error, t));
};
